import type {
  NightEvent,
  DayEvent,
  SheriffClaim,
  PlayerAssignment,
} from '../../types/game';

interface GameTimelineProps {
  nights: NightEvent[];
  days: DayEvent[];
  sheriffClaims: SheriffClaim[];
  assignments: PlayerAssignment[];
  focusedSeat: number | null;
  visibleDay: number; // 0 = all
}

function seatRole(seat: number, assignments: PlayerAssignment[]) {
  return assignments.find((a) => a.seat === seat)?.role ?? 'grey';
}

function roleColor(seat: number, assignments: PlayerAssignment[]): string {
  const role = seatRole(seat, assignments);
  if (role === 'black') return 'text-mafia-crimson font-semibold';
  if (role === 'sheriff') return 'text-mafia-gold font-semibold';
  if (role === 'red') return 'text-red-400';
  return 'text-mafia-paper-dim';
}

/* ── Vote group ──────────────────────────────────────────────────────── */
interface VoteGroup {
  target: number;
  voters: number[];
  isEvicted: boolean;
}

function buildVoteGroups(day: DayEvent): VoteGroup[] {
  const map: Record<number, number[]> = {};
  for (const v of day.votes) {
    (map[v.target] ??= []).push(v.voter);
  }
  return Object.entries(map)
    .map(([target, voters]) => ({
      target: Number(target),
      voters,
      isEvicted: day.evictedPlayer === Number(target),
    }))
    .sort((a, b) => a.target - b.target);
}

/* ── Night section ───────────────────────────────────────────────────── */
function NightSection({
  night,
  assignments,
  focusedSeat,
}: {
  night: NightEvent;
  assignments: PlayerAssignment[];
  focusedSeat: number | null;
}) {
  const isFocused = focusedSeat === night.killedPlayer;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-mono uppercase tracking-widest text-mafia-muted">
          Ночь {night.night}
        </span>
        <div className="flex-1 h-px bg-mafia-border/40" />
      </div>
      <div
        className={`
          inline-flex items-center gap-2 self-start px-3 py-1.5 rounded border text-xs font-sans tabular-nums
          border-mafia-crimson/40 bg-mafia-crimson/10 text-mafia-crimson
          ${isFocused ? 'ring-1 ring-mafia-gold' : ''}
        `}
      >
        <span className="text-[10px]">☽</span>
        <span>Убит</span>
        <span className={`font-bold ${roleColor(night.killedPlayer, assignments)}`}>
          №&nbsp;{night.killedPlayer}
        </span>
      </div>
    </div>
  );
}

/* ── Sheriff reveal card ─────────────────────────────────────────────── */
function SheriffRevealCard({
  claim,
  allClaims,
  nights,
  assignments,
  focusedSeat,
}: {
  claim: SheriffClaim;
  allClaims: SheriffClaim[];
  nights: NightEvent[];
  assignments: PlayerAssignment[];
  focusedSeat: number | null;
}) {
  const claimantFocused = focusedSeat === claim.claimant;
  const checkFocused = claim.checks.some((c) => c.checkedPlayer === focusedSeat);
  const isFocused = claimantFocused || checkFocused;

  // Завещание: игрок убит в Ночь N-1 и вскрывается на Дне N
  // Порядок: День N → Ночь N, поэтому на Дне N доступны проверки ночей 1..N-1
  const isTestament = nights.some(
    (n) => n.killedPlayer === claim.claimant && n.night === claim.day - 1,
  );
  // Продолжение: от этого игрока уже было вскрытие в предыдущий день
  const isContinuation = allClaims.some(
    (c) => c.claimant === claim.claimant && c.day < claim.day,
  );

  const icon = isTestament ? '⚑' : '★';
  const label = isTestament
    ? 'завещание'
    : isContinuation
      ? 'добавил проверку'
      : 'вскрылся Шерифом';

  return (
    <div
      className={`
        flex flex-col gap-1.5 p-2.5 rounded border text-xs font-sans tabular-nums
        ${isTestament ? 'border-amber-700/40 bg-amber-950/10' : 'border-mafia-border bg-mafia-card'}
        ${isFocused ? 'ring-1 ring-mafia-gold/60' : ''}
      `}
    >
      {/* Header */}
      <div className="flex items-center gap-1.5">
        <span className={isTestament ? 'text-amber-400' : 'text-mafia-paper-dim'}>{icon}</span>
        <span className={`font-bold ${roleColor(claim.claimant, assignments)}`}>
          №&nbsp;{claim.claimant}
        </span>
        <span className="text-mafia-muted">{label}</span>
        {isTestament && (
          <span className="ml-auto text-[9px] font-mono text-amber-400 border border-amber-700/40 bg-amber-900/20 px-1.5 py-0.5 rounded uppercase tracking-widest">
            завещание
          </span>
        )}
      </div>

      {/* Checks */}
      {claim.checks.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pl-4">
          {claim.checks.map((check) => {
            const targetFocused = focusedSeat === check.checkedPlayer;
            const isBlack = check.result === 'black';
            return (
              <span
                key={`${check.night}-${check.checkedPlayer}`}
                className={`
                  inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px]
                  ${isBlack
                    ? 'bg-mafia-crimson/15 text-mafia-crimson border border-mafia-crimson/30'
                    : 'bg-red-950/20 text-red-400 border border-red-800/30'
                  }
                  ${targetFocused ? 'ring-1 ring-mafia-gold' : ''}
                `}
              >
                <span className="text-mafia-muted">Н{check.night}:</span>
                <span className={`font-bold ${roleColor(check.checkedPlayer, assignments)}`}>
                  №&nbsp;{check.checkedPlayer}
                </span>
                <span>→</span>
                <span>{isBlack ? 'чёрный' : 'красный'}</span>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── Vote card ───────────────────────────────────────────────────────── */
function VoteCard({
  group,
  assignments,
  focusedSeat,
}: {
  group: VoteGroup;
  assignments: PlayerAssignment[];
  focusedSeat: number | null;
}) {
  const targetFocused = focusedSeat === group.target;
  const voterFocused = focusedSeat !== null && group.voters.includes(focusedSeat);
  const isFocused = targetFocused || voterFocused;

  return (
    <div
      className={`
        flex flex-col gap-1 p-2 rounded border text-xs font-sans tabular-nums transition-colors
        ${group.isEvicted
          ? 'border-amber-700/50 bg-amber-950/20'
          : 'border-mafia-border/60 bg-mafia-card/50'
        }
        ${isFocused ? 'ring-1 ring-mafia-gold/60' : ''}
      `}
    >
      {/* Target */}
      <div className="flex items-center gap-1.5">
        <span className="text-mafia-muted text-[9px]">За</span>
        <span className={`font-bold text-sm ${roleColor(group.target, assignments)}`}>
          №&nbsp;{group.target}
        </span>
        {group.isEvicted && (
          <span className="ml-auto text-[9px] text-amber-400 border border-amber-700/40 bg-amber-900/20 px-1.5 py-0.5 rounded">
            выбыл
          </span>
        )}
      </div>

      {/* Voters */}
      <div className="flex flex-wrap gap-1 pl-1">
        {group.voters.map((voter) => (
          <span
            key={voter}
            className={`
              px-1 py-0.5 rounded text-[10px]
              ${focusedSeat === voter ? 'bg-mafia-gold/20 text-mafia-gold' : roleColor(voter, assignments)}
            `}
          >
            {voter}
          </span>
        ))}
        <span className="text-mafia-muted/50 text-[10px] ml-0.5">({group.voters.length})</span>
      </div>
    </div>
  );
}

/* ── Day section ─────────────────────────────────────────────────────── */
function DaySection({
  day,
  sheriffClaims,
  nights,
  assignments,
  focusedSeat,
}: {
  day: DayEvent;
  sheriffClaims: SheriffClaim[];
  nights: NightEvent[];
  assignments: PlayerAssignment[];
  focusedSeat: number | null;
}) {
  const groups = buildVoteGroups(day);
  const dayClaims = sheriffClaims.filter((c) => c.day === day.day);

  return (
    <div className="flex flex-col gap-2">
      {/* Header */}
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-mono uppercase tracking-widest text-mafia-muted">
          День {day.day}
        </span>
        <div className="flex-1 h-px bg-mafia-border/40" />
      </div>

      {/* Sheriff reveals (happen at start of day) */}
      {dayClaims.map((claim) => (
        <SheriffRevealCard
          key={`${claim.claimant}-${claim.day}`}
          claim={claim}
          allClaims={sheriffClaims}
          nights={nights}
          assignments={assignments}
          focusedSeat={focusedSeat}
        />
      ))}

      {/* Vote cards */}
      {groups.length > 0 && (
        <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
          {groups.map((g) => (
            <VoteCard
              key={g.target}
              group={g}
              assignments={assignments}
              focusedSeat={focusedSeat}
            />
          ))}
        </div>
      )}

      {/* No eviction note */}
      {day.evictedPlayer === null && groups.length > 0 && (
        <span className="text-[10px] font-sans text-mafia-muted pl-1">Никто не выбыл</span>
      )}
    </div>
  );
}

/* ── Main timeline ───────────────────────────────────────────────────── */
export function GameTimeline({
  nights,
  days,
  sheriffClaims,
  assignments,
  focusedSeat,
  visibleDay,
}: GameTimelineProps) {
  const maxPhase = Math.max(
    nights.length > 0 ? Math.max(...nights.map((n) => n.night)) : 0,
    days.length > 0 ? Math.max(...days.map((d) => d.day)) : 0,
  );

  const phases: number[] = [];
  for (let i = 1; i <= maxPhase; i++) phases.push(i);

  const visiblePhases = visibleDay === 0 ? phases : phases.slice(0, visibleDay);

  return (
    <div className="flex flex-col gap-5">
      {visiblePhases.map((phase) => {
        const night = nights.find((n) => n.night === phase);
        const day = days.find((d) => d.day === phase);
        return (
          <div key={phase} className="flex flex-col gap-3">
            {day && (
              <DaySection
                day={day}
                sheriffClaims={sheriffClaims}
                nights={nights}
                assignments={assignments}
                focusedSeat={focusedSeat}
              />
            )}
            {night && (
              <NightSection
                night={night}
                assignments={assignments}
                focusedSeat={focusedSeat}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
